// Team Output — top-of-Impact rollup of what the quality team shipped, split by
// US / Evizi sub-team + an AI-adoption gauge. Ported from qa-live (QA-2286) and
// adapted to this app's dark theme + inline styles.

import { getQaTeamMember } from './pulseConfig.js'
import { PULSE_COLORS } from './pulseConfig.js'

const TILES = [
    { key: 'bugsCreated', label: 'Bugs Found', emoji: '🐛', accent: '#EF4444' },
    { key: 'bugsClosed', label: 'Bugs Closed', emoji: '✅', accent: '#10B981' },
    { key: 'storiesAutomated', label: 'Stories Automated', emoji: '🤖', accent: '#3B82F6' },
    { key: 'qaStoriesClosed', label: 'QA Stories Closed', emoji: '📋', accent: '#8B5CF6' },
    { key: 'aiCompletedTests', label: 'AI Tests Authored', emoji: '✨', accent: '#06B6D4' },
    { key: 'mergeRequests', label: 'MRs Merged', emoji: '🦁', accent: '#F59E0B' },
]

function sum(members, key) {
    return members.reduce((acc, m) => acc + (Number(m[key]) || 0), 0)
}

export default function QaImpactSummary({ members, timeRangeLabel = 'Last 12 months', headerAction }) {
    const qa = members.filter((m) => m.qaTeam)

    const totalScore = sum(qa, 'score')
    const aiAdopters = qa.filter((m) => m.aiCompletedTests > 0).length
    const aiAdoptionPct = qa.length > 0 ? Math.round((aiAdopters / qa.length) * 100) : 0

    const teamFor = (m) => getQaTeamMember(m.name)?.team ?? 'us'

    const teams = ['us', 'evizi'].map((key) => {
        const roster = qa.filter((m) => teamFor(m) === key)
        return {
            key,
            label: key === 'us' ? 'US Team' : 'Evizi Team',
            flag: key === 'us' ? '🇺🇸' : '🇻🇳',
            color: key === 'us' ? '#2563EB' : '#7C3AED',
            score: sum(roster, 'score'),
            headcount: roster.length,
            bugsCreated: sum(roster, 'bugsCreated'),
            storiesAutomated: sum(roster, 'storiesAutomated'),
            qaStoriesClosed: sum(roster, 'qaStoriesClosed'),
        }
    })
    const teamMax = Math.max(1, ...teams.map((t) => t.score))

    const r = 34
    const c = 2 * Math.PI * r
    const gaugeOffset = c * (1 - aiAdoptionPct / 100)

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                border: `1px solid ${PULSE_COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    padding: '16px 20px',
                    background: 'linear-gradient(90deg, rgba(99,102,241,0.18), rgba(59,130,246,0.12))',
                    borderBottom: `1px solid ${PULSE_COLORS.border}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '12px',
                }}
            >
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '18px', margin: 0 }}>
                        📊 Team Output
                    </h3>
                    <p style={{ marginTop: '2px', fontSize: '12px', color: PULSE_COLORS.textMuted }}>
                        What the quality team shipped · {timeRangeLabel}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                        style={{ textAlign: 'right', cursor: 'help' }}
                        title="Total team impact score — the sum of every engineer's weighted quality work (QA stories ×5, stories automated ×4, bugs closed ×3, MRs ×3, bugs found ×2, AI tests ×2, tooling commits ×2, e2e commits ×1). Same scale as the Leaderboard points."
                    >
                        <div style={{ fontSize: '24px', fontWeight: 800, color: PULSE_COLORS.text, lineHeight: 1 }}>
                            {totalScore.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginTop: '2px' }}>
                            {qa.length} engineers · team impact score ⓘ
                        </div>
                    </div>
                    {headerAction}
                </div>
            </div>

            <div style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '12px',
                    }}
                >
                    {TILES.map((t) => (
                        <div
                            key={t.key}
                            style={{
                                borderRadius: '8px',
                                border: `1px solid ${PULSE_COLORS.border}`,
                                background: PULSE_COLORS.bgInner,
                                padding: '12px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: PULSE_COLORS.textMuted,
                                }}
                            >
                                <span>{t.emoji}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {t.label}
                                </span>
                            </div>
                            <div style={{ marginTop: '4px', fontSize: '24px', fontWeight: 800, color: t.accent }}>
                                {sum(qa, t.key).toLocaleString()}
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    style={{
                        marginTop: '16px',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
                        gap: '16px',
                    }}
                >
                    <div style={{ borderRadius: '8px', border: `1px solid ${PULSE_COLORS.border}`, padding: '16px' }}>
                        <div
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: PULSE_COLORS.textSubtle,
                                marginBottom: '12px',
                            }}
                        >
                            By Team
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {teams.map((t) => (
                                <div key={t.key}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontSize: '12px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        <span style={{ fontWeight: 600, color: PULSE_COLORS.text }}>
                                            {t.flag} {t.label}
                                            <span
                                                style={{
                                                    marginLeft: '4px',
                                                    fontWeight: 400,
                                                    color: PULSE_COLORS.textSubtle,
                                                }}
                                            >
                                                · {t.headcount}
                                            </span>
                                        </span>
                                        <span style={{ fontWeight: 600, color: PULSE_COLORS.textMuted }}>
                                            {t.score.toLocaleString()} pts
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            height: '8px',
                                            borderRadius: '9999px',
                                            background: PULSE_COLORS.bgInner,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                height: '100%',
                                                borderRadius: '9999px',
                                                width: `${Math.max(4, Math.round((t.score / teamMax) * 100))}%`,
                                                backgroundColor: t.color,
                                                transition: 'all 0.2s',
                                            }}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            marginTop: '4px',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '2px 12px',
                                            fontSize: '11px',
                                            color: PULSE_COLORS.textMuted,
                                        }}
                                    >
                                        <span>🐛 {t.bugsCreated} found</span>
                                        <span>🤖 {t.storiesAutomated} automated</span>
                                        <span>📋 {t.qaStoriesClosed} QA stories</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        style={{
                            borderRadius: '8px',
                            border: `1px solid ${PULSE_COLORS.border}`,
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <div
                            style={{
                                alignSelf: 'flex-start',
                                fontSize: '11px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: PULSE_COLORS.textSubtle,
                                marginBottom: '4px',
                            }}
                        >
                            AI Adoption
                        </div>
                        <svg width="96" height="96" viewBox="0 0 96 96" style={{ margin: '4px 0' }}>
                            <circle cx="48" cy="48" r={r} fill="none" strokeWidth="10" stroke={PULSE_COLORS.bgInner} />
                            <circle
                                cx="48"
                                cy="48"
                                r={r}
                                fill="none"
                                stroke="#06B6D4"
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={c}
                                strokeDashoffset={gaugeOffset}
                                transform="rotate(-90 48 48)"
                            />
                            <text
                                x="48"
                                y="46"
                                textAnchor="middle"
                                fill="#22D3EE"
                                style={{ fontSize: 20, fontWeight: 800 }}
                            >
                                {aiAdoptionPct}%
                            </text>
                            <text
                                x="48"
                                y="62"
                                textAnchor="middle"
                                fill={PULSE_COLORS.textSubtle}
                                style={{ fontSize: 9 }}
                            >
                                of team
                            </text>
                        </svg>
                        <p style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, margin: 0 }}>
                            {aiAdopters} of {qa.length} engineers authored an{' '}
                            <code style={{ fontSize: '10px' }}>@aiCompleted</code> test
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
